import React, { Component } from 'react';

import JSONLDUtils from 'common/util/JsonLdUtils';

import Helmet from './Helmet';

const Title = 'Integrations | Canny';
const Description =
  'Connect Canny with apps in your existing workflow. Including integrations with Intercom, Slack, Jira, and Zapier.';
const Image = 'https://canny.io/images/canny-social.png';
const IntegrationsUrl = 'https://canny.io/integrations';

const jsonLdGraph = JSONLDUtils.createGraph([
  JSONLDUtils.createWebPage({
    '@id': `${IntegrationsUrl}#webpage`,
    url: IntegrationsUrl,
    name: 'Integrations - Canny',
    description:
      'Explore all Canny integrations, including Intercom, Zendesk, Jira, Salesforce, and more to centralize and manage feedback across your tool stack.',
    inLanguage: 'en',
    about: { '@id': 'https://canny.io/#app' },
    isPartOf: { '@id': 'https://canny.io/#website' },
  }),
  JSONLDUtils.createListItemItemList(`${IntegrationsUrl}#list`, 'Canny Integrations', [
    {
      name: 'Intercom',
      description: 'Send customer feedback from Intercom conversations directly into Canny.',
    },
    {
      name: 'Zendesk',
      description: 'Sync customer feedback from Zendesk tickets into Canny for better visibility.',
    },
    {
      name: 'Salesforce',
      description:
        'Connect Salesforce account-level feedback with Canny to guide product priorities.',
    },
    {
      name: 'HubSpot',
      description:
        'Capture customer insights from HubSpot and send them into Canny to align feedback with CRM data.',
    },
    {
      name: 'Help Scout',
      description:
        'Send customer feedback from Help Scout conversations into Canny for tracking and prioritization.',
    },
    {
      name: 'Gong',
      description:
        'Capture feedback from Gong meeting notes and sync into Canny for actionable insights.',
    },
    {
      name: 'Freshdesk',
      description: 'Collect customer feedback from Freshdesk tickets and centralize in Canny.',
    },
    {
      name: 'Jira',
      description:
        'Integrate with Jira to sync feature requests and bug reports directly into your development workflow.',
    },
    {
      name: 'ClickUp',
      description:
        'Push feature requests and feedback from Canny into ClickUp to match your project tracking.',
    },
    {
      name: 'GitHub',
      description:
        'Link Canny feedback to GitHub issues to improve visibility between support, product, and engineering.',
    },
    {
      name: 'Asana',
      description: 'Sync requests from Canny into Asana to track and manage product work.',
    },
    {
      name: 'Linear',
      description:
        'Integrate Canny with Linear to sync feedback-driven tasks with your development pipeline.',
    },
    {
      name: 'Azure DevOps',
      description:
        'Send feedback from Canny into Azure DevOps to integrate product input into engineering workflows.',
    },
    {
      name: 'Google Workspace (SSO)',
      description: 'Enable SSO for Google Workspace users to access Canny securely.',
    },
    {
      name: 'Okta SSO',
      description: 'Use Okta for single sign-on to manage user access to Canny securely.',
    },
    {
      name: 'OneLogin SSO',
      description: 'Enable enterprise SSO using OneLogin to centralize user authentication.',
    },
    {
      name: 'Microsoft Entra ID',
      description:
        'Allow users to sign in to Canny with Microsoft Entra ID / Azure AD credentials.',
    },
    {
      name: 'OpenID Connect (OIDC)',
      description: 'Enable login through OpenID Connect–compliant identity providers.',
    },
    {
      name: 'Slack',
      description:
        'Push feedback to Canny directly from Slack channels to streamline collaboration.',
    },
    {
      name: 'Microsoft Teams',
      description: 'Send feedback from Teams conversations into Canny to capture internal input.',
    },
    {
      name: 'Discord',
      description: 'Allow community feedback from Discord chats to flow into Canny.',
    },
    {
      name: 'Google Analytics',
      description: 'Track user behavior within Canny using Google Analytics.',
    },
    { name: 'Zoom', description: 'Capture feedback from Zoom meeting transcripts into Canny.' },
    {
      name: 'Zapier',
      description: 'Automate workflows and connect Canny with thousands of apps using Zapier.',
    },
    {
      name: 'Autopilot (Zapier)',
      description:
        'Use Zapier to send Autopilot insights and actions between Canny and other tools.',
    },
  ]),
  JSONLDUtils.createBreadcrumbList([
    { name: 'Home', item: 'https://canny.io/' },
    { name: 'Integrations', item: IntegrationsUrl },
  ]),
]);

export default class IntegrationsLandingHelmet extends Component {
  render() {
    return (
      <Helmet
        title={Title}
        meta={[
          { name: 'description', content: Description },

          { property: 'og:image', content: Image },
          { property: 'og:title', content: Title },
          { property: 'og:description', content: Description },
          { property: 'og:url', content: IntegrationsUrl },
          { property: 'og:type', content: 'website' },

          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:site', content: '@cannyHQ' },
          { name: 'twitter:title', content: Title },
          { name: 'twitter:description', content: Description },
          { name: 'twitter:image', content: Image },
        ]}>
        <script type="application/ld+json">{JSONLDUtils.toString(jsonLdGraph)}</script>
      </Helmet>
    );
  }
}
